import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import { Row, Col } from "antd";
import { useSegment } from "@dreambigger/shared/src/hooks";
import animations from "@dreambigger/shared/src/components/board/styles/animations.module.scss";
import { isInAdminMode } from "../AdminApplicationWrapper";
import { StepContentSectionV2Props } from "./StepContentSectionV2";
import { SuccessContentV2, StepContentWrapper } from "./index";

const StepContentSectionV2NoImage = ({
  children,
  brand,
  title,
  subTitle,
  description,
  stepProgress,
  previousSlug,
  stepType,
  assets,
  hideProgressBar,
  heroImage,
  heroText,
  heroLogo,
}: StepContentSectionV2Props) => {
  const router = useRouter();
  const { flowId, stepSlug } = router.query;
  const segment = useSegment();
  const [isSuccessComplete, setIsSuccessComplete] = useState(false);
  const isAdminMode = isInAdminMode();

  const trackAnalytics = () => {
    segment.track({ action: "Logo Click", label: `Logo - ${stepSlug}` });
  };

  useEffect(() => {
    // Check if success is complete after mount to avoid hydration mismatch
    if (stepType === "success" && router.query.submission === "complete") {
      setIsSuccessComplete(true);
    }
  }, [stepType, router.query.submission]);

  const handleClick = () => {
    router.push({
      query: {
        ...router.query,
        flowId,
        stepSlug: previousSlug,
      },
    });
    segment.track({
      action: "Button Click",
      label: `Back - ${stepSlug as string}`,
    });
  };

  // SUCCESS STEP - Use V2 success component
  if (isSuccessComplete) {
    return (
      <SuccessContentV2
        brand={brand}
        title={title}
        subTitle={subTitle}
        description={description}
        assets={assets}
        heroImage={heroImage}
        isAdminMode={isAdminMode}
      >
        {children}
      </SuccessContentV2>
    );
  }

  // REGULAR STEPS - V3 centered card layout with bg-cta
  const maxWidth = "2200px";

  return (
    <div
      className="flex flex-column justify-between min-h-100vh lg_block"
      style={{
        maxWidth: maxWidth,
        margin: "0 auto",
        backgroundColor: "transparent",
      }}
    >
      {/* Main content area - responsive layout */}
      <Row
        className="lg_bg-cta lg_pv-6"
        gutter={[48, 0]}
        style={{ margin: 0, overflowX: "hidden" }}
      >
        {/* LEFT SPACER - Desktop only */}
        <Col xs={0} lg={6} />

        {/* CENTER COLUMN - Main content card */}
        <Col xs={24} lg={12}>
          <div className={`bg-white lg_s-2 lg_br-4 ${animations.fadeIn_f}`}>
            <div className="ph-3 md_ph-8 lg_ph-7 xl_ph-8 pv-4 md_pv-5 lg_pv-6">
              <StepContentWrapper
                brand={brand}
                title={title}
                description={description}
                stepProgress={stepProgress}
                previousSlug={previousSlug}
                hideProgressBar={hideProgressBar}
                handleClick={handleClick}
                trackAnalytics={trackAnalytics}
              >
                {children}
              </StepContentWrapper>
            </div>
          </div>
        </Col>

        {/* RIGHT SPACER - Desktop only, contains heroText card */}
        <Col xs={0} lg={6}>
          {heroText && (
            <div
              className={`dn lg_db bg-primary textPrimary s-2 br-4 pa-6 ${animations.fadeIn_f}`}
              style={{
                maxWidth: "400px",
              }}
            >
              {/* Desktop: Larger logo in card layout */}
              {heroLogo && (
                <img
                  src={heroLogo}
                  alt="logo"
                  className="mb-4"
                  style={{ maxHeight: "80px" }}
                />
              )}
              <div
                className="f-4 lh-3"
                dangerouslySetInnerHTML={{
                  __html: heroText,
                }}
              />
            </div>
          )}
        </Col>
      </Row>

      {/*
        Mobile HeroText - sticky footer at bottom of viewport
        NOTE: This is intentionally duplicated from the desktop version above.
        We render {children} (form fields) only ONCE to avoid validation race conditions,
        but render heroText/heroLogo twice with different styling for mobile vs desktop.
        The duplication of static HTML content is acceptable and necessary for the layout.
      */}
      {heroText && (
        <div className="flex lg_dn bg-primary textPrimary items-center pa-4">
          {/* Mobile: Smaller logo in horizontal footer bar */}
          {heroLogo && (
            <img
              src={heroLogo}
              className="mr-4"
              alt="logo"
              style={{ maxWidth: "60px" }}
            />
          )}
          <div
            className="f-2 lh-2 ma-0"
            dangerouslySetInnerHTML={{
              __html: heroText,
            }}
          />
        </div>
      )}
    </div>
  );
};

export default StepContentSectionV2NoImage;
